/**
 * Response serializers mirroring the backend.
 *
 * Each function here is a port of its counterpart in the API so the demo
 * returns exactly what production returns:
 *   - toPickupResponse          → backend/src/utils/pickupResponse.ts
 *   - toSeasonResponse          → backend/src/utils/seasonResponse.ts
 *   - toTierResponse            → backend/src/routes/tiers.routes.ts
 *   - toSubscriptionResponse    → backend/src/routes/subscriptions.routes.ts
 *   - toHarvestProductResponse  → backend/src/routes/harvestProducts.routes.ts
 *   - toHarvestLogResponse      → backend/src/routes/harvestLogs.routes.ts
 *   - toOrderResponse           → backend/src/routes/orders.routes.ts
 *   - toProfilePayload          → backend/src/controllers/auth.controller.ts
 *
 * If a backend serializer changes, change its twin here.
 */

import type {
  DemoHarvestLog,
  DemoHarvestProduct,
  DemoOrder,
  DemoPickup,
  DemoPickupLocation,
  DemoSeason,
  DemoSubscription,
  DemoTier,
  DemoUser,
} from "./fixtures";

export function toPickupResponse(pickup: DemoPickup, user?: DemoUser) {
  return {
    id: pickup._id,
    userId: pickup.userId,
    firstName: user?.firstName ?? "",
    surname: user?.surname ?? "",
    phoneNumber: user?.phoneNumber ?? "",
    email: user?.email ?? "",
    pickupLocationId: pickup.pickupLocationId,
    signupPickupLocationId: pickup.signupPickupLocationId,
    pickupDay: pickup.pickupDay,
    subscriptionTierId: pickup.subscriptionTierId,
    subscriptionPaymentStatus: pickup.subscriptionPaymentStatus ?? "pending",
    paidAt: pickup.paidAt ?? "",
    friendPickupFirstName: pickup.friendPickupFirstName ?? null,
    friendPickupLastName: pickup.friendPickupLastName ?? null,
    friendPickupPhone: pickup.friendPickupPhone ?? null,
    createdAt: pickup.createdAt,
    updatedAt: pickup.updatedAt,
  };
}

export function toPickupLocationResponse(location: DemoPickupLocation) {
  return {
    _id: location._id,
    name: location.name,
    address: location.address,
    latitude: location.latitude,
    longitude: location.longitude,
    days: location.days,
    ...(location.undeletable !== undefined && {
      undeletable: location.undeletable,
    }),
    createdAt: location.createdAt,
    updatedAt: location.updatedAt,
  };
}

export function toTierResponse(tier: DemoTier) {
  return {
    id: tier._id,
    subscriptionId: tier.subscriptionId,
    name: tier.name ?? "",
    price: tier.price ?? 0,
    oldCsaMemberPrice: tier.oldCsaMemberPrice ?? 0,
    spots: tier.spots,
    dependsOn: tier.dependsOn,
    takesFrom: tier.takesFrom ?? [],
  };
}

export function toSubscriptionResponse(subscription: DemoSubscription) {
  return {
    id: subscription._id,
    name: subscription.name ?? "",
    spots: subscription.spots,
    isCombo: subscription.isCombo,
    isActive: subscription.isActive ?? false,
  };
}

export function toHarvestProductResponse(product: DemoHarvestProduct) {
  return {
    id: product._id,
    name: product.name ?? "",
    quantity: product.quantity ?? 0,
    price: product.price ?? 0,
    discount: product.discount ?? null,
    createdAt: product.createdAt,
    updatedAt: product.updatedAt,
  };
}

export function toHarvestLogResponse(log: DemoHarvestLog) {
  return {
    logId: log._id,
    startDate: log.startDate ?? "",
    endDate: log.endDate ?? "",
    comment: log.comment ?? "",
    products: log.products ?? [],
  };
}

export function toOrderResponse(order: DemoOrder, user?: DemoUser) {
  return {
    id: order._id,
    businessId: user?._id ?? "",
    businessName: user?.companyName ?? "",
    companyRegistrationNumber: order.companyRegistrationNumber ?? "",
    phone: user?.phoneNumber ?? "",
    email: user?.email ?? "",
    totalPrice: order.totalPrice ?? 0,
    deliveryMethod: order.deliveryMethod ?? "pickup",
    ...(order.companyAddress != null && { companyAddress: order.companyAddress }),
    ...(order.companyPostalCode != null && {
      companyPostalCode: order.companyPostalCode,
    }),
    ...(order.companyCity != null && { companyCity: order.companyCity }),
    ...(order.shippingPrice != null && { shippingPrice: order.shippingPrice }),
    status: order.status ?? "Pending",
    products: order.products ?? [],
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
  };
}

export function toSeasonResponse(season: DemoSeason) {
  return {
    id: season._id,
    areWeInSeason: season.areWeInSeason ?? false,
    isBusinessOpen: season.isBusinessOpen ?? false,
    applicationPeriod: season.applicationPeriod ?? "application-closed",
    freeDeliveryThreshold: season.freeDeliveryThreshold ?? 1000,
    businessDeliveryPrice: season.businessDeliveryPrice ?? 80,
    csaDeliveryPrice: season.csaDeliveryPrice ?? 80,
    checkboxes: season.checkboxes,
    waitlist: season.waitlist ?? [],
    createdAt: season.createdAt,
    updatedAt: season.updatedAt,
  };
}

export function toProfilePayload(
  user: DemoUser,
  hasCSASubscription: boolean,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    email: user.email,
    phoneNumber: user.phoneNumber ?? "",
    role: user.role ?? "",
    subscriptionHistory: user.subscriptionHistory ?? [],
    hasCSASubscription,
  };

  if (user.role === "business") {
    const payload: Record<string, unknown> = { ...base };
    payload.companyName = user.companyName ?? "";
    payload.companyRegistrationNumber = user.companyRegistrationNumber ?? "";
    payload.address = user.address ?? "";
    payload.postalCode = user.postalCode ?? "";
    payload.city = user.city ?? "";
    if (user.shippingHeavyPrice != null)
      payload.shippingHeavyPrice = user.shippingHeavyPrice;
    if (user.shippingLightPrice != null)
      payload.shippingLightPrice = user.shippingLightPrice;
    return payload;
  }

  return {
    ...base,
    firstName: user.firstName,
    secondName: user.secondName ?? "",
    surname: user.surname,
  };
}

export function toUserListItem(user: DemoUser, hasCSASubscription: boolean) {
  const name = [user.firstName, user.secondName].filter(Boolean).join(" ");
  return {
    userId: user._id,
    firstName: user.firstName,
    secondName: user.secondName,
    surname: user.surname,
    name,
    lastName: user.surname,
    email: user.email,
    role: user.role ?? undefined,
    phoneNumber: user.phoneNumber,
    emailVerified: user.emailVerified,
    createdAt: user.createdAt,
    hasCSASubscription,
    subscriptionHistory: user.subscriptionHistory,
  };
}

export function toLoginResponse(user: DemoUser, token: string) {
  const userPayload: Record<string, unknown> = {
    id: user._id,
    firstName: user.firstName,
    secondName: user.secondName,
    surname: user.surname,
    email: user.email,
    phoneNumber: user.phoneNumber,
    role: user.role,
  };
  if (user.role === "business" && user.companyRegistrationNumber) {
    userPayload.companyRegistrationNumber = user.companyRegistrationNumber;
  }
  return { message: "Login successful", token, user: userPayload };
}
